import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommunityVideo } from '../../../api/community/types';
import { getImageUrl } from '../../../utils/imageUrl';
import CommunityActionMenu from './CommunityActionMenu';
import './SocialCard.css';

interface SocialCardProps {
  video: CommunityVideo;
  onEdit?: (video: CommunityVideo) => void;
  onPublish?: (video: CommunityVideo) => void;
  onUnpublish?: (video: CommunityVideo) => void;
  onDelete?: (video: CommunityVideo) => void;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

const SocialCard: React.FC<SocialCardProps> = ({
  video,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu
}) => {
  const navigate = useNavigate();
  const thumbnailUrl = video.thumbnailUrl || video.contentUrl;
  const imageUrl = getImageUrl(thumbnailUrl);
  const hasErrorRef = useRef(false);

  // Reset error ref when image URL changes
  useEffect(() => {
    hasErrorRef.current = false;
  }, [imageUrl]);

  // Filter out "string" placeholder value from backend
  const profilePic = video.metadata?.userProfilePic && video.metadata.userProfilePic !== 'string' 
    ? video.metadata.userProfilePic 
    : undefined;
  const isVideoPost = video.mediaType === 'video';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      const kValue = num / 1000;
      if (kValue >= 1 && kValue < 10) {
        return `${kValue.toFixed(1)}k`;
      }
      return `${Math.floor(kValue)}k`;
    }
    return num.toString();
  };

  // Determine if it's a carousel (multiple images) - only show if actual carousel data exists
  // For now, we don't have carousel data, so we won't show the indicator
  const isCarousel = false; // Set to true only when we have actual carousel data (e.g., video.images?.length > 1)
  const carouselIndex = null; // Only set when we have actual carousel data
  const carouselTotal = null; // Only set when we have actual carousel data

  const getCategoryPath = (category: string): string => {
    if (category === 'action') return 'action';
    if (category === 'level_up') return 'level_up';
    if (category === 'social') return 'social';
    return 'social'; // Default
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the menu
    if ((e.target as HTMLElement).closest('.social-card-menu')) {
      return;
    }
    const categoryPath = getCategoryPath(video.category);
    navigate(`/community/${categoryPath}/${video._id}`);
  };

  return (
    <div className="social-card" onClick={handleCardClick}>
      <div className="social-card-header">
        <div className="social-user-info">
          {profilePic && (
            <img
              src={getImageUrl(profilePic)}
              alt={video.metadata?.userName || 'User'}
              className="social-user-avatar"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div className="social-user-details">
            <div className="social-user-name">{video.metadata?.userName || 'SFL Oranje Academy'}</div>
            <div className="social-post-date">{formatDate(video.createdAt)}</div>
          </div>
        </div>
        <div className="social-card-menu" onClick={(e) => e.stopPropagation()}>
          <CommunityActionMenu
            video={video}
            onEdit={onEdit}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
            onDelete={onDelete}
            isOpen={isMenuOpen}
            onToggle={onToggleMenu}
            onClose={onCloseMenu}
          />
        </div>
      </div>

      <div className="social-card-image-container">
        <img
          src={hasErrorRef.current ? '/placeholder-video.png' : (imageUrl || '/placeholder-video.png')}
          alt={video.title}
          className="social-card-image"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            // Only set placeholder if we haven't already tried it
            if (target.src !== window.location.origin + '/placeholder-video.png' && !hasErrorRef.current) {
              hasErrorRef.current = true;
              target.src = '/placeholder-video.png';
            } else {
              // If placeholder also fails, hide the image to prevent infinite loop
              target.style.display = 'none';
            }
          }}
        />
        {isVideoPost && (
          <div className="social-video-play-overlay">
            <div className="social-video-play-button">
              <i className="ph-play-fill"></i>
            </div>
          </div>
        )}
        {carouselIndex && carouselTotal && (
          <div className="social-carousel-indicator">
            {carouselIndex}/{carouselTotal}
          </div>
        )}
      </div>

      <div className="social-card-content">
        <h5 className="social-card-title">{video.title}</h5>
        <p className="social-card-description">{video.description}</p>
        <div className="social-card-stats">
          <span className="social-stat-item">
            <i className="ph-heart"></i>
            {formatNumber(video.likes)}
          </span>
          <span className="social-stat-item">
            <i className="ph-chat-circle"></i>
            {formatNumber(video.comments)}
          </span>
          <span className="social-stat-item">
            <i className="ph-paper-plane-tilt"></i>
            {formatNumber(video.shares)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SocialCard;

